import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, CheckCircle2, CreditCard } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { waLink } from "@/lib/site";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0F1418] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]";

const EMPTY = { name: "", phone: "", email: "", pickup: "Rayagada", destination: "", travel_date: "", vehicle: "", days: 1, notes: "" };

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function BookingModal({ open, onClose, vehicles, packages, prefillDestination }) {
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [paid, setPaid] = useState(false);
  const [payConfig, setPayConfig] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...EMPTY, destination: prefillDestination || "", vehicle: vehicles[0]?.name || "" }));
      setSaved(null);
      setPaid(false);
      setError("");
      api.get("/payments/config").then((r) => setPayConfig(r.data)).catch(() => setPayConfig({ enabled: false }));
    }
  }, [open, prefillDestination, vehicles]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/bookings", { ...form, days: Number(form.days) });
      setSaved(data);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const payAdvance = async () => {
    setPaying(true);
    setError("");
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load the payment window. Please try again.");
      const { data: order } = await api.post("/payments/create-order", { booking_id: saved.id });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Jay Jagannath Tour & Travels",
        description: `Advance for ${saved.destination}`,
        prefill: { name: saved.name, contact: saved.phone, email: saved.email || undefined },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", { booking_id: saved.id, ...resp });
            setPaid(true);
          } catch (err) {
            setError("Payment received but verification failed — we will confirm manually on WhatsApp.");
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (err) {
      setError(fmtErr(err));
      setPaying(false);
    }
  };

  const waMessage = `Hello Jay Jagannath Tour & Travels, I just submitted a booking request on your website.\nName: ${form.name}\nPhone: ${form.phone}\nTrip: ${form.destination}\nPickup: ${form.pickup}\nDate: ${form.travel_date}\nVehicle: ${form.vehicle}\nDays: ${form.days}${paid ? `\nAdvance of ₹${payConfig?.advance_amount || 500}: PAID` : ""}${form.notes ? `\nNotes: ${form.notes}` : ""}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-white/10 bg-[#171F26] p-8 sm:rounded-3xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#D4AF37]">Book your trip</p>
                <h3 className="mt-2 font-serif text-2xl font-semibold">Tell us the journey</h3>
              </div>
              <button data-testid="booking-close" onClick={onClose} aria-label="Close booking form" className="rounded-full border border-white/15 p-2 text-white/60 transition-colors hover:border-[#D4AF37] hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {saved ? (
              <div className="mt-8 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#D4AF37]" />
                <h4 className="mt-4 font-serif text-xl font-semibold">{paid ? "Advance paid — see you soon!" : "Request received"}</h4>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  {paid
                    ? "Your advance payment is confirmed. We'll share driver details on WhatsApp before your trip."
                    : "Your booking request is saved. Pay a small advance now, or confirm on WhatsApp — we reply within minutes."}
                </p>
                {!paid && payConfig?.enabled && (
                  <button
                    data-testid="booking-pay-btn"
                    onClick={payAdvance}
                    disabled={paying}
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-[#0F1418] transition-transform hover:scale-[1.03] disabled:opacity-60"
                  >
                    <CreditCard className="h-4 w-4" /> {paying ? "Opening payment…" : `Pay ₹${payConfig.advance_amount} Advance`}
                  </button>
                )}
                <a
                  href={waLink(waMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-whatsapp-confirm"
                  className={`mt-3 flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-transform hover:scale-[1.03] ${
                    paid || !payConfig?.enabled
                      ? "bg-[#D4AF37] text-[#0F1418]"
                      : "border border-white/20 text-white/85 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  <MessageCircle className="h-4 w-4" /> Confirm on WhatsApp
                </a>
                {error && (
                  <p data-testid="booking-payment-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">{error}</p>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="booking-name-input" required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} />
                  <input data-testid="booking-phone-input" required placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} inputMode="tel" />
                </div>
                <input data-testid="booking-email-input" type="email" placeholder="Email (optional — we'll send a trip reminder)" value={form.email} onChange={set("email")} className={inputCls} />
                <div className="grid gap-4 sm:grid-cols-2">
                  <select data-testid="booking-pickup-select" value={form.pickup} onChange={set("pickup")} className={inputCls}>
                    {["Rayagada", "Bhubaneswar", "Visakhapatnam", "Berhampur", "Other"].map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                  <input data-testid="booking-date-input" required type="date" value={form.travel_date} onChange={set("travel_date")} className={inputCls} />
                </div>
                <select data-testid="booking-destination-select" required value={form.destination} onChange={set("destination")} className={inputCls}>
                  <option value="" disabled>
                    Choose a package or destination
                  </option>
                  {packages.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name} ({p.duration})
                    </option>
                  ))}
                  <option value="Custom trip">Custom trip — I'll describe it below</option>
                </select>
                <div className="grid gap-4 sm:grid-cols-2">
                  <select data-testid="booking-vehicle-select" value={form.vehicle} onChange={set("vehicle")} className={inputCls}>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.name}>
                        {v.name} · {v.seats}
                      </option>
                    ))}
                  </select>
                  <input data-testid="booking-days-input" required type="number" min="1" max="30" value={form.days} onChange={set("days")} className={inputCls} placeholder="Days" />
                </div>
                <textarea data-testid="booking-notes-input" rows="2" placeholder="Anything else? (group size, hotel preference…)" value={form.notes} onChange={set("notes")} className={inputCls} />
                {error && (
                  <p data-testid="booking-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-[#0F1418] transition-transform hover:scale-[1.02] disabled:opacity-60"
                >
                  {loading ? "Saving…" : "Submit Booking Request"}
                </button>
                <p className="text-center text-xs text-white/40">No advance payment needed — we confirm everything on WhatsApp first.</p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
